import React from "react";

// Simple, mobile-friendly height picker with tap buttons
interface HeightPickerProps {
  feet: number;
  inches: number;
  onFeetChange: (feet: number) => void;
  onInchesChange: (inches: number) => void;
}

export const HeightPicker: React.FC<HeightPickerProps> = ({
  feet,
  inches,
  onFeetChange,
  onInchesChange,
}) => {
  const feetOptions = [4, 5, 6, 7];
  const inchesOptions = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div className="space-y-4">
      {/* Feet - Large tap buttons */}
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold text-center">
          Feet
        </div>
        <div className="flex justify-center gap-2">
          {feetOptions.map((f) => (
            <button
              key={f}
              onClick={() => onFeetChange(f)}
              className={`
                w-16 h-14 rounded-xl font-bold text-lg transition-all duration-200
                ${feet === f
                  ? 'bg-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95'
                }
              `}
            >
              {f}'
            </button>
          ))}
        </div>
      </div>

      {/* Inches - Scrollable button row */}
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold text-center">
          Inches
        </div>
        <div className="flex justify-center gap-1.5 flex-wrap">
          {inchesOptions.map((i) => (
            <button
              key={i}
              onClick={() => onInchesChange(i)}
              className={`
                w-11 h-11 rounded-lg font-semibold text-sm transition-all duration-200
                ${inches === i
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95'
                }
              `}
            >
              {i}"
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Simple, mobile-friendly birthdate picker with native selects
interface BirthdatePickerProps {
  month?: number;
  day?: number;
  year?: number;
  onMonthChange: (month: number) => void;
  onDayChange: (day: number) => void;
  onYearChange: (year: number) => void;
}

export const BirthdatePicker: React.FC<BirthdatePickerProps> = ({
  month,
  day,
  year,
  onMonthChange,
  onDayChange,
  onYearChange,
}) => {
  // Use abbreviated month names to fit mobile screens
  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 80 }, (_, i) => currentYear - 15 - i);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const selectStyles = `
    w-full px-2 py-3 rounded-xl border-2 border-gray-200 bg-white
    text-gray-800 font-medium text-sm sm:text-base
    focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none
    appearance-none cursor-pointer
    transition-all duration-200 truncate
  `;

  return (
    <div className="grid grid-cols-3 gap-3">
      {/* Month */}
      <div>
        <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 font-semibold block text-center">
          Month
        </label>
        <div className="relative">
          <select
            value={month || ""}
            onChange={(e) => onMonthChange(Number(e.target.value))}
            className={selectStyles}
          >
            <option value="" disabled>--</option>
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Day */}
      <div>
        <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 font-semibold block text-center">
          Day
        </label>
        <div className="relative">
          <select
            value={day || ""}
            onChange={(e) => onDayChange(Number(e.target.value))}
            className={selectStyles}
          >
            <option value="" disabled>--</option>
            {days.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Year */}
      <div>
        <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 font-semibold block text-center">
          Year
        </label>
        <div className="relative">
          <select
            value={year || ""}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className={selectStyles}
          >
            <option value="" disabled>--</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export a placeholder for WheelPicker in case it's imported elsewhere
const WheelPicker: React.FC<{
  options: { value: number | string; label: string }[];
  value: number | string;
  onChange: (value: number | string) => void;
  itemHeight?: number;
  visibleItems?: number;
  label?: string;
  showArrows?: boolean;
}> = () => null;

export default WheelPicker;
